// Third-party libraries
import 'reflect-metadata';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

// App API
import { ApiExecutor, API_METADATA } from '@app/core/api';
import { ApiValidationService } from '../services/api-validation.service';
import type { ApiControllerOptions } from '../types';
import { ParamMetadata, ResponseType, RouteMetadata } from '../types';

// App exceptions
import { ValidationErrorException } from '../exceptions/validation-error.exception';


export function ApiController(basePath: string = '', options?: ApiControllerOptions): ClassDecorator {
  return <TFunction extends Function>(target: TFunction): TFunction => {
    Reflect.defineMetadata(API_METADATA.BASE_PATH, basePath, target);
    if (options?.baseUrl) {
      Reflect.defineMetadata(API_METADATA.BASE_URL, options.baseUrl, target);
    }

    const controllerBaseUrl: string | undefined = options?.baseUrl;
    const routes: RouteMetadata[] = Reflect.getOwnMetadata(API_METADATA.ROUTES, target) || [];

    routes.forEach(route => {
      const originalMethod = target.prototype[route.methodName];

      target.prototype[route.methodName] = function(...args: any[]): Observable<any> {
        const apiExecutor: ApiExecutor = (this as any).apiExecutor;
        const validationService: ApiValidationService = (this as any).apiValidationService;

        if (!apiExecutor) {
          throw new Error(
            `ApiExecutor not found in ${target.name}.${route.methodName}. ` +
            `Make sure to inject ApiExecutor: constructor(public apiExecutor: ApiExecutor) {}`
          );
        }

        const paramsMetadata: ParamMetadata[] = Reflect.getOwnMetadata(API_METADATA.PARAMS, target.prototype, route.methodName) || [];
        const bodyMetadata: ParamMetadata[] = Reflect.getOwnMetadata(API_METADATA.BODY, target.prototype, route.methodName) || [];
        const queryMetadata: ParamMetadata[] = Reflect.getOwnMetadata(API_METADATA.QUERY, target.prototype, route.methodName) || [];

        const headersMetadata: Record<string, string> = Reflect.getOwnMetadata(API_METADATA.HEADERS, target.prototype, route.methodName) || {};
        const responseTypeMetadata: ResponseType = Reflect.getOwnMetadata(API_METADATA.RESPONSE_TYPE, target.prototype, route.methodName);
        const logMetadata: boolean = Reflect.getOwnMetadata(API_METADATA.LOG, target.prototype, route.methodName) || false;
        const reportProgressMetadata: boolean = Reflect.getOwnMetadata(API_METADATA.REPORT_PROGRESS, target.prototype, route.methodName) || false;
        const skipWorkspaceHeaderMetadata: boolean = Reflect.getOwnMetadata(API_METADATA.SKIP_WORKSPACE_HEADER, target.prototype, route.methodName) || false;

        let url = `${basePath}${route.path}`;
        const pathParams: { [key: string]: any } = {};
        const queryParams: { [key: string]: any } = {};
        let body: any = undefined;
        let bodyDtoClass: (new () => any) | undefined = undefined;

        paramsMetadata.forEach(param => {
          const value = args[param.index];
          if (param.key) {
            if (value === undefined || value === null) {
              throw new Error(
                `Path parameter '${param.key}' is undefined or null in ${target.name}.${route.methodName}. ` +
                `All path parameters must have a value.`
              );
            }
            pathParams[param.key] = value;

            url = url.replaceAll(`:${param.key}`, String(value));
          } else {

            if (value && typeof value === 'object') {
              Object.assign(pathParams, value);

              Object.keys(value).forEach(key => {
                if (value[key] !== undefined && value[key] !== null) {
                  url = url.replaceAll(`:${key}`, String(value[key]));
                }
              });
            }
          }
        });

        bodyMetadata.forEach(param => {
          body = args[param.index];
          bodyDtoClass = param.dtoClass;
        });

        queryMetadata.forEach(param => {
          const value = args[param.index];
          if (param.key) {
            queryParams[param.key] = value;
          } else {
            Object.assign(queryParams, value);
          }
        });

        const executeRequest = (requestBody: any): Observable<any> => {
          const requestConfig: any = {
            method: route.method,
            url: url,
            body: requestBody,
            params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
            baseUrl: controllerBaseUrl,
          };

          if (Object.keys(headersMetadata).length > 0) {
            requestConfig.headers = headersMetadata;
          }

          if (responseTypeMetadata) {
            requestConfig.responseType = responseTypeMetadata;
          }

          if (reportProgressMetadata) {
            requestConfig.reportProgress = true;
            requestConfig.observe = 'events';
          }

          if (skipWorkspaceHeaderMetadata) {
            requestConfig.skipWorkspaceHeader = true;
          }

          if (logMetadata) {
            console.log(`[API Request] ${route.method} ${url}`, {
              params: queryParams,
              body: requestBody,
              headers: headersMetadata,
              responseType: responseTypeMetadata,
              bodyType: bodyDtoClass ? `DTO Class: ${bodyDtoClass.name}` : 'Primitive/Interface (no validation)'
            });
          }

          const request$ = apiExecutor.execute(requestConfig);

          if (logMetadata) {
            return request$.pipe(
              tap({
                next: (response) => {
                  console.log(`[API Response] ${route.method} ${url}`, response);
                },
                error: (error) => {
                  console.error(`[API Error] ${route.method} ${url}`, error);
                }
              })
            );
          }

          return request$;
        };


        if (bodyDtoClass && body !== undefined) {
          if (!validationService) {
            throw new Error(
              `ApiValidationService not found in ${target.name}.${route.methodName}. ` +
              `Make sure to inject ApiValidationService: constructor(public apiValidationService: ApiValidationService) {}`
            );
          }

          return from(validationService.validateAndTransform(bodyDtoClass, body)).pipe(
            switchMap(validatedBody => executeRequest(validatedBody)),
            catchError((error) => {
              if (error instanceof ValidationErrorException) {
                console.error(`[Validation Error] ${route.method} ${url}`, {
                  dto: error.context?.dtoClass,
                  messages: error.messages,
                  details: error.context?.details,
                  transformWarning: error.context?.transformWarning,
                });
              }
              return throwError(() => error);
            })
          );
        }


        return executeRequest(body);
      };
    });

    return target;
  };
}

